import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { faCheck, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/index';
import {
  NoteReviewWarning,
  NoteSelect,
  PatientConsentBlurb,
  PMSNoteActions,
  TooltipButton,
  buttonStyling,
} from '@repo/videa-notes';

import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';

import { useMemo } from 'react';

import { renderTemplate } from '@repo/videa-components/src/ui/variable-textarea/useContentEditable';

import { useVideaNotesCoreContext } from '../../context/VideaNotesCoreContext';
import {
  useNoteData,
  useNoteHandlers,
  useNoteStatus,
  useVideaNotesNoteResultContext,
} from '../../context/VideaNotesNoteResultContext';
import { useRecorderState } from '../../context/VideaNotesRecordingContext';
import { getCurrentTemplate } from '../../helpers';
import { NoteEditorWrapper } from '../NoteEditorWrapper';
import { NotesPageNavigationBlurb } from '../NotesPageNavigationBlurb';

/**
 * Note selector header component.
 * Shows just the note selector dropdown in the header area.
 */
export const NoteResultHeader = () => {
  const { recorderStatus, mediaBlobUrl } = useRecorderState();
  const { clinicalNotesData, selectedNote } = useNoteData();
  const { onSelectedNote } = useNoteHandlers();

  const isRecorderStopped =
    recorderStatus === 'stopped' || recorderStatus === 'idle';

  if (!selectedNote) {
    return null;
  }

  // Only show selector when we have notes and recorder is stopped
  if (!isRecorderStopped || mediaBlobUrl || clinicalNotesData.length === 0) {
    return null;
  }

  return (
    <div className="vai-w-1/2 vai-pr-1">
      <NoteSelect
        clinicalNotes={clinicalNotesData}
        selectedNote={selectedNote}
        onSelectedNote={onSelectedNote}
      />
    </div>
  );
};

/**
 * Note result body component.
 * Shows the generated note with editing capabilities and action buttons.
 */
export const NoteResultBody = () => {
  // Variables
  const coreContext = useVideaNotesCoreContext();
  const { patient, voiceNotesSource, onCloseSheet } = coreContext;

  const {
    selectedNote,
    noteContent,
    patientConsentBlurb,
    variableDefinitions,
  } = useNoteData();

  const { showCopyTooltip } = useVideaNotesNoteResultContext();

  // Booleans
  const { isVoiceNotesConsentEnabled } = coreContext;

  const {
    isNoteReadOnly,
    isNoteSavedToPMS,
    isNoteBeingSavedToPMS,
    isSavingNoteUpdate,
    isSavingNoteToPMS,
    isRetryingWriteback,
    hasWritebackEnabled,
    hasBeenEdited,
  } = useNoteStatus();

  // Functions
  const {
    onNoteChange,
    onNoteClick,
    onCopyText,
    onCreateAdditionalNote,
    onSaveNoteToPMS,
    onRetryWritebackToPMS,
    onResetNote,
  } = useNoteHandlers();

  // Compute rendered summary for Copy button
  // VariableTextarea computes its own copy text with current variableValues (including unsaved changes)
  // For the Copy button, we compute it using default values from variableDefinitions
  const renderedSummary = useMemo(() => {
    if (!selectedNote) {
      return '';
    }

    const currentTemplate = getCurrentTemplate(selectedNote);
    const isTemplateNote = Boolean(
      currentTemplate && currentTemplate.length > 0,
    );

    if (isTemplateNote && variableDefinitions.length > 0) {
      // For template notes, build variable value map from definitions and render template
      const variableValueMap = Object.fromEntries(
        variableDefinitions.map((def) => [def.varName, def.defaultValue]),
      );
      return renderTemplate(noteContent, variableValueMap);
    } else {
      // For non-template notes, use current noteContent or fall back to backend data
      return (
        noteContent || selectedNote.editedSummary || selectedNote.summary || ''
      );
    }
  }, [selectedNote, noteContent, variableDefinitions]);

  if (!selectedNote) {
    return null;
  }

  return (
    <div className="vai-mt-4 vai-flex vai-flex-col vai-flex-1 vai-min-h-0 vai-overflow-hidden">
      {/* Static header: Review warning */}
      <div className="vai-flex-shrink-0 vai-px-1 vai-pb-4">
        <NoteReviewWarning />
      </div>

      {/* Flexible middle: Note editor */}
      <div className="vai-flex vai-flex-col vai-flex-1 vai-min-h-0 vai-overflow-hidden">
        <NoteEditorWrapper
          noteContent={noteContent}
          noteId={selectedNote?.id}
          feedbackTraceId={selectedNote?.feedbackTraceId}
          existingRating={selectedNote?.rating}
          isNoteReadOnly={isNoteReadOnly}
          isNoteSavedToPMS={isNoteSavedToPMS}
          isNoteBeingSavedToPMS={isNoteBeingSavedToPMS}
          isSavingNoteUpdate={isSavingNoteUpdate}
          hasBeenEdited={hasBeenEdited}
          variableDefinitions={variableDefinitions}
          onNoteChange={onNoteChange}
          onNoteClick={onNoteClick}
          onResetNote={onResetNote}
        />
      </div>

      {/* Patient consent blurb - shown when writeback is enabled */}
      {patientConsentBlurb && isVoiceNotesConsentEnabled && (
        <div className="vai-my-3 vai-flex-shrink-0 vai-px-1">
          <PatientConsentBlurb blurb={patientConsentBlurb} />
        </div>
      )}

      {/* Static footer: Action buttons */}
      <div className="vai-flex vai-items-center vai-justify-between vai-w-full vai-pb-4 vai-pt-6 vai-px-1 vai-flex-shrink-0 vai-bg-white">
        {/* Left: Navigation Blurb */}
        <NotesPageNavigationBlurb
          voiceNotesSource={voiceNotesSource}
          onCloseSheet={onCloseSheet}
        />

        {/* Right: Action Buttons */}
        <NoteActionButtons
          patient={patient}
          selectedNote={selectedNote}
          noteContent={renderedSummary}
          showCopyTooltip={showCopyTooltip}
          isSavingNoteToPMS={isSavingNoteToPMS}
          isRetryingWriteback={isRetryingWriteback}
          onCopyText={onCopyText}
          onCreateAdditionalNote={onCreateAdditionalNote}
          onSaveNoteToPMS={onSaveNoteToPMS}
          onRetryWritebackToPMS={onRetryWritebackToPMS}
        />
      </div>
    </div>
  );
};

interface NoteActionButtonsProps {
  patient:
    | {
        pmsPatientId?: string;
        firstName?: string;
        lastName?: string;
        displayName?: string;
        sourcePatientId?: string;
        imagingPatientId?: string;
        videaId?: string;
        dob?: string;
      }
    | undefined;
  selectedNote: ClinicalNote;
  noteContent: string;
  showCopyTooltip: boolean;
  isSavingNoteToPMS: boolean;
  isRetryingWriteback: boolean;
  onCopyText: (text: string) => void;
  onCreateAdditionalNote: () => void;
  onSaveNoteToPMS: () => void;
  onRetryWritebackToPMS: () => void;
}

const NoteActionButtons = ({
  patient,
  selectedNote,
  noteContent,
  showCopyTooltip,
  isSavingNoteToPMS,
  isRetryingWriteback,
  onCopyText,
  onCreateAdditionalNote,
  onSaveNoteToPMS,
  onRetryWritebackToPMS,
}: NoteActionButtonsProps) => {
  // Check if writeback is eligible based on API response
  const isWritebackEligible = selectedNote.isWritebackEligible;

  return (
    <div className="vai-space-x-2 vai-pr-1 vai-ml-auto">
      {isWritebackEligible ? (
        <>
          <TooltipButton
            icon={faPlus}
            tooltip="Start new recording"
            variant="outline"
            onClick={onCreateAdditionalNote}
          />
          <TooltipButton
            icon={showCopyTooltip ? faCheck : faCopy}
            tooltip={showCopyTooltip ? 'Copied!' : 'Copy note to clipboard'}
            variant="outline"
            onClick={() => onCopyText(noteContent)}
            iconClassName={showCopyTooltip ? 'vai-text-green-600' : ''}
          />
          <PMSNoteActions
            patient={patient}
            selectedNote={selectedNote}
            isSavingNoteToPMS={isSavingNoteToPMS}
            isRetryingWriteback={isRetryingWriteback}
            onSaveNoteToPMS={onSaveNoteToPMS}
            onRetryWritebackToPMS={onRetryWritebackToPMS}
          />
        </>
      ) : (
        <div className="vai-flex vai-gap-2">
          <Button onClick={onCreateAdditionalNote} variant="outline">
            <span>
              <FontAwesomeIcon icon={faPlus} />
            </span>
            <Trans>New Note</Trans>
          </Button>
          <Button
            onClick={() => onCopyText(noteContent)}
            className={`${buttonStyling} hover:vai-ring-primary/50`}
          >
            <span>
              <FontAwesomeIcon
                icon={showCopyTooltip ? faCheck : faCopy}
                className={showCopyTooltip ? 'vai-text-green-600' : ''}
              />
            </span>
            {showCopyTooltip ? (
              <Trans>Copied!</Trans>
            ) : (
              <Trans>Copy Note</Trans>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
